/**
 * Onix Cart — Quick-add popup
 * ───────────────────────────
 * Opens the "+" product-card popup: variant picker, flocage inputs,
 * quantity stepper, optimistic inject into the cart drawer, background
 * API call to /cart/add.js.
 *
 * Pulls shared helpers from `window.OnixCart` (defined in
 * cart-drawer-utils.js, must load before this file) and reads the
 * cart-drawer bridges (`window.__onixDrawerBody`, `window.__onixOpenDrawer`,
 * etc.) that cart-drawer.js exposes on the window when it initializes.
 *
 * Load order: utils → cart-drawer → this → discount
 */
(function initQuickAdd() {
  const OnixCart = window.OnixCart || {};
  const escapeHtml    = OnixCart.escapeHtml    || ((s) => String(s || ''));
  const loggedFetch   = OnixCart.loggedFetch   || fetch.bind(window);
  const showCartError = OnixCart.showCartError || ((m) => console.warn('[onix-cart]', m));
  /* Reject any URL whose protocol could execute script — only allow
     http(s)/relative/protocol-relative. Defends against a tampered
     featured_image like "javascript:..." injected via a hostile API. */
  const safeUrl = (u) => {
    const s = String(u || '').trim();
    if (!s) return '';
    if (/^(?:javascript|data|vbscript|file):/i.test(s)) return '';
    return s;
  };

  const POPUP_ID = 'onix-quick-add-popup';
  const _dr = document.querySelector('[data-cart-drawer-root]');
  const qt = {
    addToCart: (_dr && _dr.dataset.tAddToCart) || 'ADD TO CART',
    chooseSize: (_dr && _dr.dataset.tChooseSize) || 'Choose your size:',
    flocage: (_dr && _dr.dataset.tFlocage) || 'Flocage',
    flocageOptional: (_dr && _dr.dataset.tFlocageOptional) || '(optional)',
    flocageNamePh: (_dr && _dr.dataset.tFlocageNamePh) || 'e.g. Mbappé',
    flocageNumberPh: (_dr && _dr.dataset.tFlocageNumberPh) || 'e.g. 7',
    flocageBelowPh: (_dr && _dr.dataset.tFlocageBelowPh) || 'e.g. the best',
    error: (_dr && _dr.dataset.tError) || 'Error',
    /* ⛔ le « + » des cartes produit affichait « Code invalide » quand l'ajout
       échouait — même défaut que le bouton de la fiche, trouvé le 30/08. */
    addError: (_dr && _dr.dataset.tAddError) || "Ajout impossible — réessaie.",
  };

  /* Active AbortController for the currently mounted popup. Every
     listener attached in createPopup passes { signal: _abortCtrl.signal }
     so a single _abortCtrl.abort() in removePopup detaches them all in
     one call — no per-listener tracking, no leaks. */
  let _abortCtrl = null;

  function removePopup() {
    /* Abort any pending listeners attached to the previous popup so
       they're garbage-collected with the DOM node. */
    if (_abortCtrl) {
      _abortCtrl.abort();
      _abortCtrl = null;
    }
    const existing = document.getElementById(POPUP_ID);
    if (existing) existing.remove();
    document.body.style.overflow = '';
  }

  /* overridePriceCents : prix d'affichage imposé par l'appelant (ex. le bloc
     « 2e maillot », qui annonce le prix APRÈS remise). Sans lui, on affiche le
     prix catalogue comme partout ailleurs. */
  function createPopup(product, overridePriceCents) {
    removePopup();
    /* Fresh controller for this popup instance — all listeners below
       must pass { signal: _abortCtrl.signal } to be cleaned up. */
    _abortCtrl = new AbortController();
    const _signal = _abortCtrl.signal;
    const variants = product.variants || [];
    const hasVariants = variants.length > 1;
    /* EUR: format FR exact "24,99€". Autres devises: Intl natif (symbole-avant +
       point), identique au présentment Shopify `| money` (ex. "£22.00"). */
    const fmt = (cents) => {
      const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      const amount = (cents || 0) / 100;
      if (cur === 'EUR') return amount.toFixed(2).replace('.', ',') + '€';
      try { return new Intl.NumberFormat('en', { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' }).format(amount); }
      catch (e) { try { return new Intl.NumberFormat('en', { style: 'currency', currency: cur }).format(amount); } catch (e2) { return amount.toFixed(2) + ' ' + cur; } }
    };

    /* All visual styling lives in assets/cart-drawer-quickadd.css. This
       function only composes the DOM structure with class names — no
       inline styles, no inline event handlers. Hover/focus states are
       handled natively by CSS pseudo-classes. */
    const overlay = document.createElement('div');
    overlay.id = POPUP_ID;

    const thumbUrl = product.featured_image
      ? safeUrl(product.featured_image.replace(/(\.\w+)(\?|$)/, '_180x$1$2'))
      : '';

    // Titre court : enlever les couleurs (dernier segment)
    const rawTitle = product.title || '';
    const titleParts = rawTitle.replace(/,/g, '').split(/\s[-\u2013]\s/);
    const shortTitle = titleParts.length > 1 ? titleParts.slice(0, -1).join(' \u2013 ') : rawTitle;
    const title = escapeHtml(shortTitle);

    /* Prix imposé → on l'affiche et on barre le prix catalogue : le client ne
       doit jamais revoir 24,99 € après qu'on lui a annoncé le 2e maillot moins
       cher, sinon il refait le calcul et l'offre perd sa force. */
    const showPrice = Number.isFinite(overridePriceCents) ? overridePriceCents : product.price;
    const comparePrice = Number.isFinite(overridePriceCents)
      ? product.price
      : (product.compare_at_price > product.price ? product.compare_at_price : 0);
    const priceHtml = comparePrice > showPrice
      ? `<span class="onix-qa-price">${fmt(showPrice)}</span><s class="onix-qa-compare">${fmt(comparePrice)}</s>`
      : `<span class="onix-qa-price">${fmt(showPrice)}</span>`;

    // Flocage toujours affiché
    const flocageHtml = `
      <div class="onix-qa-group">
        <p class="onix-qa-label">${escapeHtml(qt.flocage)} <span class="onix-qa-label__hint">${escapeHtml(qt.flocageOptional)}</span></p>
        <input type="text" data-qa-flocage="Nom" class="onix-qa-input" placeholder="${escapeHtml(qt.flocageNamePh)}">
        <input type="text" data-qa-flocage="Num\u00E9ro" class="onix-qa-input" placeholder="${escapeHtml(qt.flocageNumberPh)}" inputmode="numeric" maxlength="3" pattern="[0-9]*" data-numeric-max="3">
        <input type="text" data-qa-flocage="Nom sous le num\u00E9ro" class="onix-qa-input" placeholder="${escapeHtml(qt.flocageBelowPh)}">
      </div>
    `;

    // Tailles en cercles — pas de gestion de stock
    let sizesHtml = '';
    if (hasVariants) {
      sizesHtml = `<div class="onix-qa-group">
        <p class="onix-qa-label onix-qa-label--dim">${escapeHtml(qt.chooseSize)}</p>
        <div class="onix-qa-sizes">`;
      variants.forEach((v, i) => {
        const isSelected = i === 0;
        sizesHtml += `<button type="button" class="onix-qa-size" data-qa-variant="${v.id}" data-qa-price="${v.price}" data-qa-compare="${v.compare_at_price || ''}"${isSelected ? ' data-qa-selected' : ''}>${escapeHtml(v.title)}</button>`;
      });
      sizesHtml += '</div></div>';
    }

    // Quantité
    const qtyHtml = `
      <div class="onix-qa-qty">
        <div class="onix-qa-qty__wrap">
          <button type="button" class="onix-qa-qty__btn" data-qa-qty-minus aria-label="Decrease quantity">−</button>
          <span class="onix-qa-qty__value" data-qa-qty-value aria-live="polite">1</span>
          <button type="button" class="onix-qa-qty__btn" data-qa-qty-plus aria-label="Increase quantity">+</button>
        </div>
      </div>
    `;

    overlay.innerHTML = `
      <div class="onix-qa-panel">
        <button type="button" class="onix-qa-close" data-qa-close aria-label="Close">✕</button>

        <!-- Header -->
        <div class="onix-qa-header">
          ${thumbUrl ? `<img src="${thumbUrl}" class="onix-qa-thumb" alt="">` : ''}
          <div class="onix-qa-meta">
            <h3 class="onix-qa-title">${title}</h3>
            <div class="onix-qa-price-row">${priceHtml}</div>
          </div>
        </div>

        ${sizesHtml}
        ${flocageHtml}
        ${qtyHtml}

        <button type="button" class="onix-qa-submit" data-qa-submit data-variant-id="${variants[0] ? variants[0].id : ''}">
          ${escapeHtml(qt.addToCart)}
        </button>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    // Close — all listeners auto-cleanup via AbortController signal
    overlay.querySelector('[data-qa-close]').addEventListener('click', removePopup, { signal: _signal });
    overlay.addEventListener('click', (e) => { if (e.target === overlay) removePopup(); }, { signal: _signal });

    // Keyboard: Escape closes popup (a11y)
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') removePopup(); }, { signal: _signal });

    // Focus: move to close button on open for keyboard users
    requestAnimationFrame(() => {
      const closeBtn = overlay.querySelector('[data-qa-close]');
      if (closeBtn) closeBtn.focus();
    });

    // Quantité
    let qty = 1;
    const qtyVal = overlay.querySelector('[data-qa-qty-value]');
    overlay.querySelector('[data-qa-qty-minus]').addEventListener('click', () => {
      if (qty > 1) { qty--; qtyVal.textContent = qty; }
    }, { signal: _signal });
    overlay.querySelector('[data-qa-qty-plus]').addEventListener('click', () => {
      qty++; qtyVal.textContent = qty;
    }, { signal: _signal });

    // Size selection — visual state driven by [data-qa-selected] attribute,
    // styled in cart-drawer-quickadd.css. Handler only toggles the attribute
    // and updates the submit button's variant id.
    const variantBtns = overlay.querySelectorAll('[data-qa-variant]');
    const submitBtn = overlay.querySelector('[data-qa-submit]');
    variantBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        variantBtns.forEach(b => b.removeAttribute('data-qa-selected'));
        btn.setAttribute('data-qa-selected', '');
        if (submitBtn) submitBtn.dataset.variantId = btn.dataset.qaVariant;
      }, { signal: _signal });
    });

    // Submit — optimistic: inject item into drawer BEFORE API call
    overlay.querySelector('[data-qa-submit]').addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const properties = {};
      overlay.querySelectorAll('[data-qa-flocage]').forEach(input => {
        const val = input.value.trim();
        if (val) properties[input.dataset.qaFlocage] = val;
      });

      // Get selected variant data from the size button
      const selectedBtn = overlay.querySelector('[data-qa-variant][data-qa-selected]') || overlay.querySelector('[data-qa-variant]');
      const variantPrice = selectedBtn ? Number(selectedBtn.dataset.qaPrice) : (product.price || 0);
      const variantCompare = selectedBtn && selectedBtn.dataset.qaCompare ? Number(selectedBtn.dataset.qaCompare) : 0;
      const variantLabel = selectedBtn ? selectedBtn.textContent.trim() : '';

      /* ── Optimistic inject ── */
      const dBody = window.__onixDrawerBody ? window.__onixDrawerBody() : null;
      const countNodes = window.__onixCartCountNodes ? window.__onixCartCountNodes() : [];
      if (dBody && window.__onixBuildItemHtml) {
        const emptyState = dBody.querySelector('.onix-cart-drawer__empty');
        if (emptyState) dBody.innerHTML = '';

        // Pre-populate compare price map
        if (variantCompare && variantCompare > variantPrice) {
          if (!window.__onixCompareAtPrices) window.__onixCompareAtPrices = {};
          window.__onixCompareAtPrices[variantId] = variantCompare;
        }

        const optimisticItem = {
          key: '__optimistic_qa_' + variantId,
          variant_id: variantId,
          product_title: product.title || '',
          title: product.title || '',
          url: '/products/' + (product.handle || ''),
          image: product.featured_image || '',
          featured_image: { url: product.featured_image || '' },
          variant_title: variantLabel,
          quantity: qty,
          final_price: variantPrice,
          properties: properties,
        };
        dBody.insertAdjacentHTML('afterbegin', window.__onixBuildItemHtml(optimisticItem));
        // Defer subtotal animation until drawer is visible
        const _pd = variantPrice * qty;
        const _cd = variantCompare > variantPrice ? variantCompare * qty : variantPrice * qty;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (window.__onixOptimisticSubtotal) window.__onixOptimisticSubtotal(_pd, _cd);
        }));
        countNodes.forEach(n => { n.textContent = Number(n.textContent) + qty; });
      }

      removePopup();
      if (window.__onixOpenDrawer) window.__onixOpenDrawer();

      /* ── Fire API in background ── */
      const body = { id: parseInt(variantId), quantity: qty };
      if (Object.keys(properties).length) body.properties = properties;

      try {
        const res = await loggedFetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          showCartError(data?.message || qt.addError);
        }
        if (window.__onixRefreshCart) window.__onixRefreshCart();
      } catch (err) {
        console.warn('[onix-cart] quickadd submit failed', err);
        if (window.__onixRefreshCart) window.__onixRefreshCart();
      }
    }, { signal: _signal });
  }

  /* Outer delegated click handler — lives for the whole page lifetime
     so it does NOT use the popup's abort signal. */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.quickAdd;
    if (!handle) return;
    btn.style.pointerEvents = 'none';
    try {
      const res = await loggedFetch((window.Shopify?.routes?.root || '/') + 'products/' + handle + '.js');
      const product = await res.json();
      const forced = parseInt(btn.dataset.qaPriceCents, 10);
      createPopup(product, Number.isFinite(forced) && forced > 0 ? forced : undefined);
    } catch (err) {
      console.warn('[onix-cart] quickadd fetch failed', err);
      showCartError(qt.addError);
    }
    btn.style.pointerEvents = '';
  });
})();
